<template>
  <v-dialog v-model="dialog" max-width="600px" scrollable>
    <v-card class="rounded-lg">
      <v-toolbar flat color="primary" dark dense>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          <v-icon left small>mdi-comment-text-multiple-outline</v-icon>
          Comments for {{ application.licenseApplicationNo }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text
        class="pa-4 grey lighten-5"
        style="min-height: 300px; max-height: 600px"
      >
        <!-- Loading State -->
        <v-container
          v-if="loading"
          class="fill-height justify-center align-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
        </v-container>

        <!-- Empty State -->
        <v-container
          v-else-if="comments.length === 0"
          class="fill-height flex-column justify-center align-center"
        >
          <v-icon size="80" color="grey lighten-2"
            >mdi-message-off-outline</v-icon
          >
          <div class="text-h6 grey--text text--darken-1 mt-4">
            No comments found
          </div>
          <div class="text-caption grey--text">
            There are no comments recorded for this application.
          </div>
        </v-container>

        <!-- Comments List -->
        <v-timeline v-else dense align-top class="pa-0">
          <v-timeline-item
            v-for="(comment, index) in sortedComments"
            :key="index"
            :color="getAvatarColor(comment.userID)"
            small
            fill-dot
          >
            <template v-slot:icon>
              <span class="white--text text-caption font-weight-bold">{{
                getInitials(comment.userID)
              }}</span>
            </template>
            <v-card class="elevation-1 rounded-lg">
              <v-card-title
                class="text-caption py-2 px-3 grey lighten-4 d-flex justify-space-between"
              >
                <span
                  class="font-weight-bold text-truncate primary--text"
                  style="max-width: 150px"
                >
                  {{ formatUser(comment.userID) ? "Reviewer" : "-" }}
                </span>
                <span
                  class="grey--text text--darken-1"
                  style="font-size: 0.7rem"
                >
                  <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatDate(comment.commentDate, 8) }}
                </span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-3 text-body-2 text--primary">
                {{ comment.comment || "No content" }}
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import DateMixin from "../../../mixins/DateMixin";

export default {
  name: "LicenseApplicationComments",
  mixins: [DateMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      comments: [],
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    sortedComments() {
      return [...this.comments].sort(
        (a, b) => new Date(b.commentDate) - new Date(a.commentDate)
      );
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchComments();
      } else {
        this.comments = []; // Reset on close
      }
    },
  },
  methods: {
    async fetchComments() {
      this.loading = true;

      this.$store
        .dispatch("License/getMembershipComments", {
          $filter: `documentNo eq '${this.application.licenseApplicationNo}'`,
        })
        .then((response) => {
          this.comments = response.data.data ? response.data.data : [];
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatUser(userId) {
      if (!userId) return "Unknown";
      // Remove domain part if present (e.g., "DOMAIN\User" -> "User")
      const parts = userId.split("\\");
      return parts.length > 1 ? parts[1] : userId;
    },
    getInitials(userId) {
      const name = this.formatUser(userId);
      return name.substring(0, 2).toUpperCase();
    },
    getAvatarColor(userId) {
      const colors = [
        "primary",
        "secondary",
        "success",
        "info",
        "warning",
        "purple",
        "indigo",
        "teal",
      ];
      let hash = 0;
      for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
  },
};
</script>

<style scoped>
/* Optional: Custom scrollbar for the comments area */
.v-card__text::-webkit-scrollbar {
  width: 6px;
}

.v-card__text::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.v-card__text::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.v-card__text::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
</style>
